/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { useShowPickupAvailable } from './use-pickup-filter';
import type { ShopperSearch } from '@/scapi';
import DynamicImageProvider from '@/providers/dynamic-image';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import type { ImageContext } from '@/lib/images/resolve-image-url';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

// Responsive size of the product images in the product grid when the refinement panel is visible.
// Values are based on the grid column configuration (2 cols below `lg`, 3 cols at `lg`+) and
// refinement panel width, which only takes up space starting at `lg` (w-64 + gap-8 --> 256px + 32px = 288px).
const responsiveImageWidthsWithRefinements = [
    '44vw', // base: 2 grid columns, no refinement panel, ~(100vw - col padding) / 2 ≈ 44% of vw
    '44vw', // sm:   2 grid columns, no refinement panel, ~(100vw - col padding) / 2 ≈ 44% of vw
    '44vw', // md:   2 grid columns, no refinement panel, ~(100vw - col padding) / 2 ≈ 44% of vw
    '19vw', // lg:   3 grid columns, refinement panel, ~(100vw − 288px − col padding) / 3 ≈ 19% of vw
    '20vw', // xl:   3 grid columns, refinement panel, ~(100vw − 288px − col padding) / 3 ≈ 20% of vw
    '20vw', // 2xl:  3 grid columns, refinement panel, ~(100vw − 288px − col padding) / 3 ≈ 20% of vw
];

// Responsive size of product images when refinements panel is collapsed.
const responsiveImageWidthsWithoutRefinements = [
    '44vw', // base: 2 grid columns, no refinement panel, ~(100vw - col padding) / 2 ≈ 44% of vw
    '44vw', // sm:   2 grid columns, no refinement panel, ~(100vw - col padding) / 2 ≈ 44% of vw
    '44vw', // md:   2 grid columns, no refinement panel, ~(100vw - col padding) / 2 ≈ 44% of vw
    '30vw', // lg:   3 grid columns, no refinement panel, ~(100vw - col padding) / 3 ≈ 30% of vw
    '30vw', // xl:   3 grid columns, no refinement panel, ~(100vw - col padding) / 3 ≈ 30% of vw
    '30vw', // 2xl:  3 grid columns, no refinement panel, ~(100vw - col padding) / 3 ≈ 30% of vw
];

function NoProductsMessage({ criticalSize, nonCriticalSize }: { criticalSize: number; nonCriticalSize: number }) {
    const { t } = useTranslation('common');

    if (criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full text-center py-12">
            <p className="text-sm text-muted-foreground">{t('noProductsFound')}</p>
        </div>
    );
}

export function NonCriticalContent({
    products,
    criticalSize,
    responsiveImageWidths,
    handleProductClick,
    imageContext,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    products: ProductSearchHit[];
    criticalSize: number;
    responsiveImageWidths: string[];
    handleProductClick?: (product: ProductSearchHit) => void;
    imageContext?: ImageContext;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}) {
    return (
        <DynamicImageProvider value={{ widths: responsiveImageWidths }}>
            <NonCriticalProducts
                products={products}
                criticalSize={criticalSize}
                handleProductClick={handleProductClick}
                imageContext={imageContext}
                showPickupAvailable={showPickupAvailable}
            />
        </DynamicImageProvider>
    );
}

export function NonCriticalProducts({
    products,
    criticalSize,
    handleProductClick,
    imageContext,
    showPickupAvailable,
}: Omit<Parameters<typeof NonCriticalContent>[0], 'responsiveImageWidths'>) {
    return (
        <>
            {products.map((product) => (
                <ProductTile
                    key={product.productId}
                    product={product}
                    handleProductClick={handleProductClick}
                    showNavigationArrows
                    imageContext={imageContext}
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    showPickupAvailable={showPickupAvailable}
                />
            ))}
            <NoProductsMessage criticalSize={criticalSize} nonCriticalSize={products.length} />
        </>
    );
}

function ProductGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-1 lg:gap-x-2 gap-y-2 lg:gap-y-4">
            <ProductGridSkeletonItems count={count} />
        </div>
    );
}

export function ProductGridSkeletonItems({ count }: { count: number }) {
    return Array.from({ length: count }, (_, index) => <ProductTileSkeleton key={`grid-skeleton-${index}`} />);
}

/**
 * ProductGrid renders product tiles in a responsive grid layout, wrapping all tiles in a shared
 * context provider to reduce hydration overhead. Instead of each tile initializing its own hooks
 * (navigate, config, translation, currency), the provider initializes them once and shares them
 * via context.
 *
 * The grid accepts synchronous data for both critical (above-the-fold) and non-critical
 * (below-the-fold) products. Critical product images are loaded with high priority and eagerly.
 * For deferred loading of non-critical products, use DeferredProductGrid.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    hasRefinementsPanel = true,
    handleProductClick,
    listingCategoryId,
    isLoading = false,
    skeletonCount,
    nonCriticalSlot,
    imageContext,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable: showPickupAvailableProp,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: ProductSearchHit[];
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    listingCategoryId?: string;
    isLoading?: boolean;
    skeletonCount?: number;
    /** Deferred non-critical tiles rendered inside the stable grid container. */
    nonCriticalSlot?: ReactNode;
    /** Cloudinary image context — determines transformation dimensions */
    imageContext?: ImageContext;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const criticalData = critical ?? [];
    const l = criticalData.length;
    const responsiveImageWidths = hasRefinementsPanel
        ? responsiveImageWidthsWithRefinements
        : responsiveImageWidthsWithoutRefinements;

    // Initialize the `<DynamicImageProvider/>` behavior for the scope of this grid.
    // Out-of-the-box we make sure that the product images of all products considered critical (displayed inside a
    // `<DynamicImage/>` component) should be loaded eagerly with high priority.
    const hasSource = useCallback(() => true, []);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const pickupFromUrl = useShowPickupAvailable();
    const showPickupAvailable = showPickupAvailableProp ?? pickupFromUrl;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const loadingSkeletonCount = Math.max(criticalData.length + (nonCritical?.length ?? 0), skeletonCount ?? 0, 4);

    if (isLoading) {
        return (
            <ProductTileProvider listingCategoryId={listingCategoryId}>
                <div data-testid="product-grid-loading-state" aria-busy>
                    <ProductGridSkeleton count={loadingSkeletonCount} />
                </div>
            </ProductTileProvider>
        );
    }

    return (
        <ProductTileProvider listingCategoryId={listingCategoryId}>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-1 lg:gap-x-2 gap-y-2 lg:gap-y-4">
                {l > 0 && (
                    <DynamicImageProvider value={{ hasSource, widths: responsiveImageWidths }}>
                        {criticalData.map((product) => (
                            <ProductTile
                                key={product.productId}
                                product={product}
                                handleProductClick={handleProductClick}
                                showNavigationArrows
                                imageContext={imageContext}
                                // @sfdc-extension-line SFDC_EXT_BOPIS
                                showPickupAvailable={showPickupAvailable}
                            />
                        ))}
                    </DynamicImageProvider>
                )}
                {nonCritical && nonCritical.length > 0 && (
                    <NonCriticalContent
                        products={nonCritical}
                        criticalSize={l}
                        responsiveImageWidths={responsiveImageWidths}
                        handleProductClick={handleProductClick}
                        imageContext={imageContext}
                        // @sfdc-extension-line SFDC_EXT_BOPIS
                        showPickupAvailable={showPickupAvailable}
                    />
                )}
                {nonCriticalSlot && (
                    <DynamicImageProvider value={{ widths: responsiveImageWidths }}>
                        {nonCriticalSlot}
                    </DynamicImageProvider>
                )}
                {(skeletonCount ?? 0) > 0 &&
                    Array.from({ length: skeletonCount ?? 0 }, (_, i) => <ProductTileSkeleton key={`skeleton-${i}`} />)}
                {!skeletonCount && <NoProductsMessage criticalSize={l} nonCriticalSize={nonCritical?.length ?? 0} />}
            </div>
        </ProductTileProvider>
    );
}
